import { useEffect, useState } from "react";
import {
  DollarSign, Sparkles, Lock, MessageSquare, Compass, Gamepad2,
  ArrowUpRight, ShieldCheck, ShieldAlert, BookOpen,
} from "lucide-react";
import { api } from "@/api/client";
import MisteckPlayHub from "@/components/dashboard/MisteckPlayHub";
import DesktopEscape from "@/components/shell/DesktopEscape";
import MisteckMark from "@/components/brand/MisteckMark";

// Security guardrail #2 — only these hub keys may ever be dispatched from the
// pillar grid. Anything else is dropped before it reaches the router, blocking
// URL/param manipulation.
const ALLOWED_HUBS = new Set(["business_hub", "imagery_hub", "vault", "lifestyle_hub"]);

const PILLARS = [
  { title: "My Safe Income", desc: "Cash ledgers, expenses & automated tax receipts.",
    Icon: DollarSign, band: "MISTLIFE", accent: "#15803d", action: { type: "hub", key: "business_hub" } },
  { title: "Design Studio", desc: "Generate music, film & writing assets.",
    Icon: Sparkles, band: "MISTPLAY", accent: "#D4AF37", action: { type: "hub", key: "imagery_hub" } },
  { title: "My Private Lockbox", desc: "Secure identity & asset preservation vault.",
    Icon: Lock, band: "MISTLIFE", accent: "#222222", action: { type: "hub", key: "vault" } },
  { title: "Global Connection", desc: "Feeds, reels & room networking.",
    Icon: MessageSquare, band: "MISTPLAY", accent: "#000000", action: { type: "view", key: "feed" } },
  { title: "Mobility & Escape", desc: "Local discovery & trip planning.",
    Icon: Compass, band: "MISTLIFE", accent: "#15803d", action: { type: "hub", key: "lifestyle_hub" } },
  { title: "Colony Simulation", desc: "Build digital equity via supply-line logistics.",
    Icon: Gamepad2, band: "MISTPLAY", accent: "#D4AF37", action: { type: "game" } },
];

// Guardrail #1 & #4 — founder-only, self-polling watchdog strip. For standard
// users it renders NOTHING (removed from the DOM, no network signature). Poll
// errors fail soft to a clean status; no raw exception ever surfaces.
function WatchdogBadge({ isFounder }) {
  const [wd, setWd] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!isFounder) return;
    let timer = null;
    const load = () =>
      api.get("/admin/watchdog").then((r) => { setWd(r.data); setErr(false); }).catch(() => setErr(true));
    const start = () => { if (!timer) { load(); timer = setInterval(load, 5000); } };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => (document.visibilityState === "hidden" ? stop() : start());
    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [isFounder]);

  if (!isFounder) return null;
  const pct = wd?.halt_at ? Math.round((wd.system_ops / wd.halt_at) * 100) : 0;
  const danger = pct >= 80;
  return (
    <div data-testid="pillar-watchdog-badge"
      className="mb-3 flex items-center gap-2 border-2 border-black bg-charcoal px-3 py-2 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
      {danger ? <ShieldAlert size={14} className="text-red-400" /> : <ShieldCheck size={14} className="text-gold" />}
      <span className="font-mono text-[9px] font-black uppercase tracking-widest text-gold">System Watchdog</span>
      <span className="ml-auto font-mono text-[10px] font-black tabular-nums text-white" data-testid="pillar-watchdog-pct">
        {err ? "—" : `${pct}% · ${wd?.system_ops ?? "–"} ops`}
      </span>
    </div>
  );
}

export default function MistSixPillarHome({ onOpenHub, onNavigate, isFounder = false }) {
  const [gameOpen, setGameOpen] = useState(false);

  // DESKTOP dual-pane: collapse the Play hub when a top-level nav zone switches.
  useEffect(() => {
    const onDeskNav = () => setGameOpen(false);
    window.addEventListener("mist:desk-nav", onDeskNav);
    return () => window.removeEventListener("mist:desk-nav", onDeskNav);
  }, []);

  const launch = (action) => {
    if (action.type === "game") { setGameOpen(true); return; }
    if (action.type === "view") { onNavigate?.(action.key); return; }
    // hub — guardrail #2 whitelist gate
    if (action.type === "hub" && ALLOWED_HUBS.has(action.key)) onOpenHub?.(action.key);
  };

  if (gameOpen) {
    return <DesktopEscape z="z-[54]"><MisteckPlayHub onBack={() => setGameOpen(false)} onRouteHub={(k) => onOpenHub?.(k)} /></DesktopEscape>;
  }

  return (
    <div data-testid="six-pillar-home">
      <WatchdogBadge isFounder={isFounder} />

      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="home-brand-lockup">
          <MisteckMark size={30} data-testid="home-brand-logo" />
          <span className="text-lg font-semibold text-white lowercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>misteck</span>
        </div>
        <span className="font-mono text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Life · Play</span>
      </div>

      <h2 className="mb-3 font-serif font-black text-lg text-white tracking-tight">Your 6 Pillars</h2>

      <div className="grid grid-cols-1 gap-3">
        {PILLARS.map((p) => (
          <button
            key={p.title}
            data-testid={`pillar-${p.action.type === "hub" ? p.action.key : p.action.type === "view" ? p.action.key : p.action.type}`}
            onClick={() => launch(p.action)}
            className="group flex items-center gap-3 border-2 border-black bg-[#FAF8F5] p-3.5 text-left shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] active:translate-x-[2px] active:translate-y-[2px]">
            <span className="grid h-11 w-11 shrink-0 place-items-center border-2 border-black bg-charcoal shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
              <p.Icon size={20} strokeWidth={2.3} style={{ color: p.accent === "#222222" || p.accent === "#000000" ? "#D4AF37" : p.accent }} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="font-serif font-black text-base text-charcoal leading-none group-hover:text-[#8a6d18]">{p.title}</span>
                <span className="font-mono text-[7px] font-black uppercase tracking-wider border border-black bg-white px-1 py-0.5 text-charcoal">{p.band}</span>
              </span>
              <span className="mt-1 block font-mono text-[10px] leading-tight text-charcoal/60">{p.desc}</span>
            </span>
            <ArrowUpRight size={16} className="shrink-0 text-charcoal/50 group-hover:text-charcoal" />
          </button>
        ))}
      </div>
    </div>
  );
}
